import { repoBranchSubmoduleSchema } from "@arti/shared-source-control/indexing/branchSubmodules";
import { z } from "zod";

import type { PlanRevisionContext } from "./context";

import { coerceToJsonObject } from "../json";
import { planRevisionRepoSelectionResultSchema } from "./repoSelectionTransport";

export const planRevisionIntentSchema = z
  .object({
    comment_context: z
      .object({
        summary: z
          .string()
          .min(1)
          .describe(
            "Concise summary of the original comment requesting the plan revision.",
          )
          .nullable(),
      })
      .strict()
      .nullable(),
    confidence: z.number().min(0).max(1).nullable(),
    intent: z.string().min(1),
    notes: z.string().nullable(),
    scope: z.enum(["story", "subtasks", "single_subtask"]),
    should_proceed: z.boolean(),
  })
  .strict();

function nullishToNullable<TSchema extends z.ZodType>(schema: TSchema) {
  return z.preprocess(
    (value) => (value === undefined ? null : value),
    schema.nullable(),
  );
}

export const planRevisionIntentSchemaJson = coerceToJsonObject(
  z.toJSONSchema(planRevisionIntentSchema),
);

function nullishToUndefined<TSchema extends z.ZodType>(schema: TSchema) {
  return nullishToNullable(schema).transform((value) => value ?? undefined);
}

const planRevisionAttachmentContextSchema = z
  .object({
    description: z.string().nullable(),
    filename: z.string(),
    id: z.string(),
    mimeType: z.string().nullable(),
    size: z.number().nullable(),
    url: z.string(),
  })
  .strict();

const planRevisionStoryContextSchema = z
  .object({
    attachments: nullishToUndefined(
      z.array(planRevisionAttachmentContextSchema),
    ),
    description: nullishToUndefined(z.string()),
    id: z.string(),
    key: z.string(),
    labels: z.array(z.string()),
    priority: z.string().nullable(),
    status: z.string().nullable(),
    title: z.string(),
    type: z.string().nullable(),
    url: z.string().nullable(),
  })
  .strict();

const planRevisionRepositoriesContextSchema = z
  .object({
    selected_base_shas: z.record(z.string(), z.string()),
    selected_branches: z.record(z.string(), z.string()),
    selected_repos: z.array(z.string()),
    submodules: z
      .record(z.string(), z.array(repoBranchSubmoduleSchema))
      .optional(),
  })
  .strict();

const planItem = z
  .object({
    attachment_refs: z
      .array(z.string().min(1))
      .default([])
      .describe(
        "Attachment filenames referenced by this plan item (must match story attachments by filename).",
      ),
    description: z.string().nullable().optional(),
    intent: z.string().nullable().optional(),
    node_id: z.string().min(1),
    reference_repository_slugs: z
      .array(z.string().min(1))
      .default([])
      .describe(
        "Additional repository slugs that provide context, patterns, or interfaces needed for this task.",
      ),
    repository_slug: z
      .string()
      .min(1)
      .describe(
        "Slug of the repository where this plan item should be implemented.",
      ),
    self_assessed_risk: z.enum(["low", "medium", "high"]).optional(),
    status: z.string().nullable().optional(),
    technical_constraints: z.array(z.string()).default([]),
    title: z.string().nullable().optional(),
    tracker_issue_id: z.string().min(1).nullable().optional(),
    tracker_issue_key: z.string().min(1).nullable().optional(),
  })
  .strict();
const planItemPartial = planItem.partial();

const sizeSymbolSchema = z.string().min(1);
const unitOfWorkIdSchema = z.string().min(1);

// For diff entries, repository_slug can be null to represent "before" state when it was unset
const planItemDiffEntry = planItemPartial.extend({
  repository_slug: z
    .string()
    .min(1)
    .nullable()
    .optional()
    .describe("Nullable in diff entries to represent prior unset state."),
  self_assessed_risk: z
    .enum(["low", "medium", "high"])
    .nullable()
    .optional()
    .describe("Nullable in diff entries to represent prior unset state."),
  size_symbol: sizeSymbolSchema
    .nullable()
    .optional()
    .describe("Nullable in diff entries to represent prior unset state."),
  tracker_issue_id: z
    .string()
    .min(1)
    .nullable()
    .optional()
    .describe("Nullable in diff entries to represent prior unset state."),
  tracker_issue_key: z
    .string()
    .min(1)
    .nullable()
    .optional()
    .describe("Nullable in diff entries to represent prior unset state."),
  uow_ids: z.array(unitOfWorkIdSchema).optional(),
});

const uowStatusSchema = z.enum([
  "active",
  "done",
  "failed",
  "blocked",
  "superseded",
  "no_longer_relevant",
]);

const planRevisionUnitOfWorkSchema = z
  .object({
    acceptance_criteria: z
      .array(z.string())
      .default([])
      .describe("Completion conditions that must be met by this unit of work."),
    dependencies: z
      .array(z.string().min(1))
      .default([])
      .describe(
        "Unit of work IDs that must complete before this unit of work can start. Always provide explicitly — pass an empty list only when the UOW truly does not depend on work done by another UOW.",
      ),
    description: z
      .string()
      .describe("Detailed implementation with file paths, APIs, configs"),
    estimated_effort: z.enum(["xs", "s", "m", "l", "xl"]).nullable().optional(),
    implementation_details: z
      .string()
      .describe("Concrete implementation plan with file paths, APIs, configs"),
    primary_repository_slug: z
      .string()
      .min(1)
      .describe(
        "Primary repository that must be modified for this unit of work.",
      ),
    reference_repository_slugs: z
      .array(z.string().min(1))
      .default([])
      .describe(
        "Additional repositories that provide context, patterns, or interfaces needed for this unit of work.",
      ),
    status: uowStatusSchema.default("active"),
    status_reason: z.string().nullable().optional(),
    superseded_by_uow_ids: z.array(z.string().min(1)).default([]),
    supersedes_uow_ids: z.array(z.string().min(1)).default([]),
    technical_notes: z.array(z.string()).default([]),
    title: z.string().min(1),
    unit_of_work_id: z.string().min(1),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.status !== "active") {
      if (value.status_reason === null || value.status_reason === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "status_reason is required when status is not active.",
          path: ["status_reason"],
        });
      }
    }
    if (value.status === "superseded") {
      if (value.superseded_by_uow_ids.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "superseded_by_uow_ids must have at least one entry when status is superseded.",
          path: ["superseded_by_uow_ids"],
        });
      }
    }
  });

const planRevisionUnitOfWorkContextSchema = z
  .object({
    acceptance_criteria: z.array(z.string()).default([]),
    dependencies: z.array(z.string()).default([]),
    description: z.string(),
    estimated_effort: z.enum(["xs", "s", "m", "l", "xl"]).nullable(),
    implementation_details: z.string(),
    primary_repository_slug: z.string(),
    reference_repository_slugs: z.array(z.string()),
    status: uowStatusSchema,
    status_reason: z.string().nullable(),
    superseded_by_uow_ids: z.array(z.string()),
    supersedes_uow_ids: z.array(z.string()),
    technical_notes: z.array(z.string()),
    title: z.string(),
    unit_of_work_id: z.string(),
  })
  .strict();

const planRevisionUowPlanContextSchema = z
  .object({
    integration_notes: z.array(z.string()),
    overview: z.string(),
    plan_complexity_score: z.enum(["low", "medium", "high"]),
    prerequisites: z.array(z.string()),
    repository_context: z
      .object({
        primary_repositories: z.array(z.string()),
        reference_repositories: z.array(z.string()),
      })
      .strict(),
    trace_id: z.string(),
    units_of_work: z.array(planRevisionUnitOfWorkContextSchema),
  })
  .strict();

const planRevisionSubtaskContextSchema = z
  .object({
    attachment_refs: z.array(z.string()),
    attachments: nullishToUndefined(
      z.array(planRevisionAttachmentContextSchema),
    ),
    description: z.string().nullable(),
    intent: z.string().nullable().optional(),
    node_id: z.string(),
    reference_repository_slugs: z.array(z.string()),
    repository_slug: z.string(),
    self_assessed_risk: z.enum(["low", "medium", "high"]),
    size_symbol: sizeSymbolSchema.nullable(),
    status: z.string().nullable(),
    technical_constraints: z.array(z.string()).default([]),
    title: z.string().nullable(),
    tracker_issue_id: z.string().nullable(),
    tracker_issue_key: z.string().nullable(),
    uow_ids: z.array(unitOfWorkIdSchema).default([]),
  })
  .strict();

const planRevisionIntentContextSchema = z
  .object({
    confidence: z.number().min(0).max(1).nullable(),
    intent: z.string().nullable(),
    notes: z.string().nullable(),
    raw_intent: z.string().nullable(),
    scope: z.enum(["story", "subtasks", "single_subtask"]).nullable(),
  })
  .strict();

export const planRevisionUowPlanSchema = z
  .object({
    plan: z
      .object({
        integration_notes: z.array(z.string()).default([]),
        overview: z
          .string()
          .describe("High-level summary of entire implementation"),
        prerequisites: z.array(z.string()).default([]),
        units_of_work: z.array(planRevisionUnitOfWorkSchema),
      })
      .strict(),
    plan_complexity_score: z
      .enum(["low", "medium", "high"])
      .describe("LLM-assessed overall effort/risk for DAAO routing."),
    reasoning: z.string().nullable().optional(),
    repository_context: z
      .object({
        primary_repositories: z.array(z.string()),
        reference_repositories: z.array(z.string()).default([]),
      })
      .strict(),
    schema_version: z
      .string()
      .default("plan_revision.uow_plan.v1")
      .describe("Schema version for observability/contract enforcement."),
    trace_id: z
      .string()
      .min(1)
      .describe(
        "Unique identifier linking plan and downstream subdivision/execution.",
      ),
  })
  .strict();

const planRevisionSubtaskItem = planItem.extend({
  size_symbol: sizeSymbolSchema.describe(
    "Configured size symbol for this subtask. Use a single symbol (XS, S, SM, M, ML, L, XL) or a compound symbol joined with '+' (e.g. M+XS, L+SM) when the subtask spans multiple merged units of work.",
  ),
  uow_ids: z
    .array(unitOfWorkIdSchema)
    .min(1)
    .describe(
      "Unit of work ids assigned to this subtask. Every unit of work must be assigned exactly once across the submitted subtasks.",
    ),
});
export const planRevisionSubtasksSchema = z
  .object({
    diff: z
      .object({
        added: z.array(z.string()).default([]),
        removed: z.array(z.string()).default([]),
        updated: z
          .array(
            z
              .object({
                after: planItemDiffEntry.optional(),
                before: planItemDiffEntry.optional(),
                node_id: z.string(),
              })
              .strict(),
          )
          .default([]),
      })
      .strict()
      .optional(),
    plan_tree: z
      .object({
        story: planItem.optional(),
        subtasks: z.array(planRevisionSubtaskItem).default([]),
      })
      .strict()
      .superRefine((value, ctx) => {
        const seenLogicalIds = new Map<string, string>();
        const registerLogicalId = (
          item:
            | z.infer<typeof planItem>
            | z.infer<typeof planRevisionSubtaskItem>
            | undefined,
          path: (string | number)[],
          label: string,
        ) => {
          if (!item) {
            return;
          }
          const logicalId =
            normalizeIdentifier(item.tracker_issue_id) ??
            normalizeIdentifier(item.tracker_issue_key) ??
            normalizeIdentifier(item.node_id);
          if (!logicalId) {
            return;
          }
          const existing = seenLogicalIds.get(logicalId);
          if (existing) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${label} resolves to logical id '${logicalId}', which duplicates ${existing}.`,
              path,
            });
            return;
          }
          seenLogicalIds.set(logicalId, label);
        };

        registerLogicalId(value.story, ["story"], "story");
        value.subtasks.forEach((subtask, index) => {
          registerLogicalId(
            subtask,
            ["subtasks", index],
            `subtask ${index + 1}`,
          );
        });
      }),
    reasoning: z.string().optional(),
    schema_version: z
      .string()
      .default("plan_revision.subtasks.v1")
      .describe("Schema version for subdivision payload."),
    trace_id: z
      .string()
      .min(1)
      .optional()
      .describe("Trace identifier linking subdivision to plan."),
  })
  .strict();

const touchpointKindSchema = z.enum(["file", "folder"]);
const touchpointRoleSchema = z.enum(["modify", "create", "read", "verify"]);

const relativeTouchpointPathSchema = z
  .string()
  .min(1)
  .refine((value) => !value.includes(":"), {
    message:
      "Touchpoint path must be repository-relative and must not include repository slug.",
  });

const touchpoint = z
  .object({
    confidence: z.number().min(0).max(1).optional(),
    kind: touchpointKindSchema.describe(
      "Must match the actual filesystem entry: use 'file' for files (e.g. 'src/utils.ts') and 'folder' only for directories (e.g. 'src/components/'). Verify the type before submitting.",
    ),
    line_end: z.number().int().positive().nullable().optional(),
    line_start: z.number().int().positive().nullable().optional(),
    note: z.string().nullable().optional(),
    path: relativeTouchpointPathSchema.describe(
      "Repository-relative path that MUST exist in the repository. Verify the path exists using readFile or listDirectory before submitting. Example: 'dashboard/server/services/mcp/deliverySchemas.ts'.",
    ),
    repository_slug: z
      .string()
      .min(1)
      .describe("Repository slug that owns the referenced path."),
    role: touchpointRoleSchema.describe(
      "How this reference should be used during implementation.",
    ),
    unit_of_work_id: z.string().min(1),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.line_start !== null && value.line_start !== undefined) {
      if (value.kind !== "file") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "line_start is only valid for file touchpoints.",
          path: ["line_start"],
        });
      }
    }
    if (value.line_end !== null && value.line_end !== undefined) {
      if (value.kind !== "file") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "line_end is only valid for file touchpoints.",
          path: ["line_end"],
        });
      } else if (
        value.line_start !== null &&
        value.line_start !== undefined &&
        value.line_end < value.line_start
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "line_end must be greater than or equal to line_start.",
          path: ["line_end"],
        });
      }
    }
  });

export const planRevisionTouchpointsSchema = z
  .object({ touchpoints: z.array(touchpoint) })
  .strict();

export const planRevisionContextSchema = z
  .object({
    existing_plan: z
      .object({
        completed_subtasks: z.array(planRevisionSubtaskContextSchema),
        subtasks: z.array(planRevisionSubtaskContextSchema),
        touchpoints: z.array(touchpoint).nullable(),
        uow_plan: planRevisionUowPlanContextSchema.nullable(),
      })
      .strict(),
    repositories: planRevisionRepositoriesContextSchema,
    revision_intent: planRevisionIntentContextSchema,
    story: planRevisionStoryContextSchema.nullable(),
  })
  .strict();

export const planRevisionPlanPipelineSchema = z
  .object({
    schema_version: z
      .string()
      .default("plan_revision.plan_pipeline.v1")
      .describe("Schema version for the consolidated planning pipeline."),
    subtasks: planRevisionSubtasksSchema.describe(
      "Subtask subdivision output.",
    ),
    thread_id: z
      .string()
      .min(1)
      .optional()
      .describe(
        "Provider thread/session id used for the chained run (best-effort).",
      ),
    touchpoints: planRevisionTouchpointsSchema.describe("Touchpoints output."),
    uow_plan: planRevisionUowPlanSchema.describe("UOW plan output."),
  })
  .strict();
export const planRevisionPlanPipelineSchemaJson = coerceToJsonObject(
  z.toJSONSchema(planRevisionPlanPipelineSchema),
);

export const planRevisionRepoSelectionSchema =
  planRevisionRepoSelectionResultSchema;

export function parsePlanRevisionContext(
  value: unknown,
): PlanRevisionContext | null {
  const parsed = planRevisionContextSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function normalizeIdentifier(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
}
